import { useUserStore } from '@/stores/user'

// Track active fetch requests with their abort controllers
const activeControllers = new Set<AbortController>()

const fetchInterceptor = (window: Window) => {
  const { fetch: originalFetch } = window

  window.fetch = async (...args: Parameters<typeof fetch>): Promise<Response> => {
    const [resource, config] = args

    const controller = new AbortController()
    activeControllers.add(controller)

    const enhancedConfig = { ...config }

    if (
      enhancedConfig?.method &&
      ['put', 'post'].includes(enhancedConfig?.method?.toLocaleLowerCase()) &&
      !(enhancedConfig.body instanceof FormData)
    ) {
      enhancedConfig.headers = {
        ...(enhancedConfig.headers ?? {}),
        'Content-Type': 'application/json;charset=UTF-8',
      }
    }

    // Add abort signal, preserving existing signal if present
    if (!enhancedConfig.signal) {
      enhancedConfig.signal = controller.signal
    }

    try {
      const response = await originalFetch(resource, enhancedConfig)

      activeControllers.delete(controller)

      if (response.status === 403 && response.url.includes(location.origin) && response.headers.get('X-Session-Expired')) {
        const user = useUserStore()
        user.logout()

        activeControllers.forEach((ctrl) => {
          try {
            ctrl.abort(new DOMException('Session expired', 'AbortError'))
          } catch {
            // Ignore errors from already completed requests
          }
        })
        activeControllers.clear()

        return response
      }
      return response
    } catch (error) {
      activeControllers.delete(controller)
      throw error
    }
  }
}

export default fetchInterceptor
